'use client';

import useSWR from 'swr';
import { UIArtifact } from '@/components/artifact';
import { useCallback, useMemo } from 'react';

export const initialArtifactData: UIArtifact = {
  documentId: 'init',
  content: '',
  kind: 'text',
  title: '',
  status: 'idle',
  // isVisible is the PRIMARY switch for "is the artifact panel open".
  // /chat/[id] behavior: starts false, user toggles via "View Full
  // Analysis" button in ChatHeader. DO NOT default to true — that hides
  // the ChatHeader and breaks the top-right toggle + sidebar.
  isVisible: false,
  hideSteps: false,
  boundingBox: {
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  },
  workflowView: null,
  pendingCellTarget: null,
  // These two flags only matter when isVisible === true (artifact panel is
  // already open). They drive the 3-state layout for /alerts/[id]:
  //   false, false  → split (chat + artifact side by side — default)
  //   true,  false  → artifact-full (chat collapsed — alert page default)
  //   false, true   → chat-full (artifact collapsed)
  // For plain /chat/[id] routes both stay false, so split mode works
  // exactly as it did before.
  isChatMinimized: false,
  isArtifactMinimized: false,
};

type Selector<T> = (state: UIArtifact) => T;

export function useArtifactSelector<Selected>(selector: Selector<Selected>) {
  const { data: localArtifact } = useSWR<UIArtifact>('artifact', null, {
    fallbackData: initialArtifactData,
  });

  const selectedValue = useMemo(() => {
    if (!localArtifact) return selector(initialArtifactData);
    return selector(localArtifact);
  }, [localArtifact, selector]);

  return selectedValue;
}

export function useArtifact() {
  const { data: localArtifact, mutate: setLocalArtifact } = useSWR<UIArtifact>(
    'artifact',
    null,
    {
      fallbackData: initialArtifactData,
    },
  );

  const artifact = useMemo(() => {
    if (!localArtifact) return initialArtifactData;
    return localArtifact;
  }, [localArtifact]);

  const setArtifact = useCallback(
    (updaterFn: UIArtifact | ((currentArtifact: UIArtifact) => UIArtifact)) => {
      setLocalArtifact((currentArtifact) => {
        const artifactToUpdate = currentArtifact || initialArtifactData;

        if (typeof updaterFn === 'function') {
          return updaterFn(artifactToUpdate);
        }

        return updaterFn;
      });
    },
    [setLocalArtifact],
  );

  const { data: localArtifactMetadata, mutate: setLocalArtifactMetadata } =
    useSWR<any>(
      () =>
        artifact.documentId ? `artifact-metadata-${artifact.documentId}` : null,
      null,
      {
        fallbackData: null,
      },
    );

  return useMemo(
    () => ({
      artifact,
      setArtifact,
      metadata: localArtifactMetadata,
      setMetadata: setLocalArtifactMetadata,
    }),
    [artifact, setArtifact, localArtifactMetadata, setLocalArtifactMetadata],
  );
}
